import React from 'react';
import { OperationalZones } from '../types/calculator';
import styles from '../styles/calculator.module.css';

interface ZoneDisplayProps {
  zones: OperationalZones;
}

const ZoneDisplay: React.FC<ZoneDisplayProps> = ({ zones }) => {
  return (
    <div className={styles.zoneDisplay} role="region" aria-labelledby="zone-display-heading">
      <div className={styles.zonesContainer}>
        <div className={`${styles.zoneItem} ${styles.flightArea}`}>
          <div className={styles.zoneTitle}>
            <span className={styles.zoneStatusDot}></span>
            Flight Area
          </div>
          <div className={styles.zoneValue}>
            {zones.flightArea.toFixed(2)} {zones.unitSystem === 'imperial' ? 'ft' : 'm'}
          </div>
          <div className={styles.zoneDescription}>
            Contains entire flight path from takeoff through landing
          </div>
        </div>
        
        <div className={`${styles.zoneItem} ${styles.softGeoFence}`}>
          <div className={styles.zoneTitle}>
            <span className={styles.zoneStatusDot}></span>
            Normal (Soft) Geo-fence
          </div>
          <div className={styles.zoneValue}>
            {(zones.flightArea + zones.softGeoFence).toFixed(2)} {zones.unitSystem === 'imperial' ? 'ft' : 'm'}
          </div>
          <div className={styles.zoneDescription}>
            Total radius from center (flight area + buffer)<br />
            Buffer: {zones.softGeoFence.toFixed(2)} {zones.unitSystem === 'imperial' ? 'ft' : 'm'}
          </div>
        </div>
        
        <div className={`${styles.zoneItem} ${styles.hardGeoFence}`}>
          <div className={styles.zoneTitle}>
            <span className={styles.zoneStatusDot}></span>
            Hard Geo-fence
          </div>
          <div className={styles.zoneValue}>
            {(zones.flightArea + zones.softGeoFence + zones.hardGeoFence).toFixed(2)} {zones.unitSystem === 'imperial' ? 'ft' : 'm'}
          </div>
          <div className={styles.zoneDescription}>
            Total radius from center<br />
            Buffer: {zones.hardGeoFence.toFixed(2)} {zones.unitSystem === 'imperial' ? 'ft' : 'm'} from soft geo-fence
          </div>
        </div>
        
        <div className={`${styles.zoneItem} ${styles.freeFall}`}>
          <div className={styles.zoneTitle}>
            <span className={styles.zoneStatusDot}></span>
            Free Fall Calculation
          </div>
          <div className={styles.zoneValue}>
            {(zones.restrictedArea || 0).toFixed(2)} {zones.unitSystem === 'imperial' ? 'ft' : 'm'}
          </div>
          <div className={styles.zoneDescription}>
            Based on free fall physics<br />
            Formula: v × √(2h/g)
          </div>
        </div>
        
        <div className={`${styles.zoneItem} ${styles.minRestricted}`}>
          <div className={styles.zoneTitle}>
            <span className={styles.zoneStatusDot}></span>
            Minimum Restricted Area Increase
          </div>
          <div className={styles.zoneValue}>
            {zones.altitudeIncrease.toFixed(2)} {zones.unitSystem === 'imperial' ? 'ft' : 'm'}
          </div>
          <div className={styles.zoneDescription}>
            Minimum {zones.unitSystem === 'imperial' ? '33 feet per 100 feet' : '10 meters per 30 meters'} of altitude
          </div>
        </div>
        
        <div className={`${styles.zoneItem} ${styles.highlight} ${styles.totalRestricted}`}>
          <div className={styles.zoneTitle}>
            <span className={styles.zoneStatusDot}></span>
            Total Restricted Area
          </div>
          <div className={styles.zoneValue}>
            {(zones.flightArea + zones.softGeoFence + zones.hardGeoFence + zones.totalRestrictedArea).toFixed(2)} {zones.unitSystem === 'imperial' ? 'ft' : 'm'}
          </div>
          <div className={styles.zoneDescription}>
            Rounded: {(zones.flightArea + zones.softGeoFence + zones.hardGeoFence + zones.totalRestrictedAreaDisplay).toFixed(0)} {zones.unitSystem === 'imperial' ? 'ft' : 'm'}<br />
            Total secure perimeter radius from center<br />
            <small>Hard geofence + {Math.max(zones.restrictedArea || 0, zones.altitudeIncrease).toFixed(2)} {zones.unitSystem === 'imperial' ? 'ft' : 'm'} ({(zones.restrictedArea || 0) > zones.altitudeIncrease ? 'free fall' : 'min altitude req.'})</small>
          </div>
        </div>
      </div>
      
      {!zones.fitsInAvailableSpace && (
        <div className={styles.spaceConstraintWarning} role="alert" aria-live="assertive">
          <p>
            <strong>Warning:</strong> The calculated restricted area exceeds your available space.
            Consider adjusting your flight parameters or finding a larger operational area.
          </p>
        </div>
      )}
    </div>
  );
};

export default ZoneDisplay;
